// Real Estate hub — Agents tab (ticket 206, batch 206-3).
// Each agent card shows the licence, CE hours, cap progress and this year's production; an
// admin adds agents here, and an agent (or an admin) edits the profile and ticks the checklist.
import React, { useCallback, useEffect, useState } from 'react';
import { CircleCheck, Circle, Loader2, Plus } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import NumberInput from '../shared/NumberInput';
import PickSelect from '../shared/PickSelect';
import StateSelect from '../shared/StateSelect';
import { formatDate } from '../kit/time';
import { fmt$ } from './format';

const DAY_MS = 86400000;
const EXPIRY_WARN_DAYS = 90;

// 'expired' | 'soon' | null, by calendar day against a date-only licence expiry.
export function expiryState(iso, now = new Date()) {
  if (!iso) return null;
  const days = Math.floor((new Date(`${iso}T00:00:00Z`) - new Date(now.toISOString().slice(0, 10) + 'T00:00:00Z')) / DAY_MS);
  if (Number.isNaN(days)) return null;
  if (days < 0) return 'expired';
  return days <= EXPIRY_WARN_DAYS ? 'soon' : null;
}

export default function HubAgentsTab() {
  const auth = useAuth();
  const me = auth?.user;
  const isAdmin = me?.role === 'admin';
  const [agents, setAgents] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [production, setProduction] = useState({ year: null, agents: {} });
  const [expanded, setExpanded] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(() => {
    // Production is a nicety: a failure leaves the cards without a stats line.
    api.get('/re/agents/production').then((r) => setProduction(r.data || { year: null, agents: {} })).catch(() => {});
    return api.get('/re/agents')
      .then((r) => { setAgents(r.data); setLoadError(''); })
      .catch((e) => setLoadError(errorMessage(e, 'Could not load agents')));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loadError) {
    return (
      <div className="text-sm text-red-600" data-testid="agents-load-error">
        {loadError} <button type="button" onClick={load} className="underline">Retry</button>
      </div>
    );
  }
  if (!agents) return <div className="text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{agents.length} agents in the brokerage</div>
        {isAdmin && (
          <button onClick={() => setShowAdd(true)} data-testid="add-agent-btn" className="text-xs font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: '#6BA82E' }}>
            <Plus size={12} /> Add agent
          </button>
        )}
      </div>
      {agents.map((a) => (
        <AgentCard key={a.agent_id} agent={a} stats={production.agents?.[a.user_id]} year={production.year}
          open={expanded === a.agent_id} onToggle={() => setExpanded(expanded === a.agent_id ? null : a.agent_id)}
          canEdit={isAdmin || me?.user_id === a.user_id} onChange={load} />
      ))}
      {agents.length === 0 && <div className="bg-white rounded-2xl p-8 border border-dashed border-slate-200 text-center text-sm text-slate-400">No agents yet. {isAdmin ? 'Use Add agent — their onboarding checklist is created from the Texas template.' : 'An admin adds agents here.'}</div>}
      {showAdd && <AddAgentModal existing={agents.map((a) => a.user_id)} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AgentCard({ agent: a, stats, year, open, onToggle, canEdit, onChange }) {
  const plan = a.commission_plan || {};
  const capped = plan.model === 'cap_100';
  const capPct = capped && plan.cap_amount > 0 ? Math.min(100, Math.round(((plan.cap_reached_amount || 0) / plan.cap_amount) * 100)) : 0;
  const expiry = expiryState(a.license_expiry);
  const title = a.name || `License #${a.license_number}`;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`agent-${a.agent_id}`}>
      <button onClick={onToggle} aria-expanded={open} className="w-full flex items-center gap-4 p-4 text-left hover:bg-slate-50">
        <div className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-bold" style={{ background: '#F3FAE8', color: '#4c7f21' }}>{(a.name || a.license_number || 'A').slice(0, 1).toUpperCase()}</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate" style={{ color: '#070B5F' }}>{title}</div>
          <div className="text-xs text-slate-500">
            {a.name && `License #${a.license_number} · `}{a.license_state || 'No state'}
            {a.license_expiry && ` · expires ${formatDate(a.license_expiry)}`}
            {expiry === 'expired' && <b className="text-red-600 ml-1" data-testid={`license-expired-${a.agent_id}`}>Expired</b>}
            {expiry === 'soon' && <b className="text-amber-600 ml-1" data-testid={`license-soon-${a.agent_id}`}>Expiring soon</b>}
          </div>
          <div className="text-xs text-slate-500">
            CE {a.ce_hours_completed || 0} of {a.ce_hours_required ?? 0} hrs · Commission model: <b>{plan.model || 'split'}</b> · Onboarding: {a.onboarding_progress || 0}%
          </div>
          {stats && (
            <div className="text-xs text-slate-500 mt-0.5" data-testid={`production-${a.agent_id}`}>
              {year}: {stats.deals_closed} closed · {fmt$(stats.volume)} volume · {fmt$(stats.gross_commission)} gross · {fmt$(stats.net_to_agent)} net to agent
            </div>
          )}
          {!stats && <div className="text-xs text-slate-400 mt-0.5" data-testid={`production-${a.agent_id}`}>{year ? `${year}: no closed deals yet` : ''}</div>}
          {capped && (
            <div className="mt-1.5 max-w-xs" data-testid={`cap-${a.agent_id}`}>
              <div className="text-[11px] text-slate-500 mb-1">Cap: {fmt$(plan.cap_reached_amount)} of {fmt$(plan.cap_amount)} ({capPct}%){capPct >= 100 && ' — reached'}</div>
              <Progress value={capPct} aria-label="Cap progress" className="h-1.5" />
            </div>
          )}
        </div>
        {a.onboarding_complete ? <CircleCheck size={18} className="text-emerald-500 shrink-0" /> : <Circle size={18} className="text-slate-300 shrink-0" />}
      </button>
      {open && (
        <>
          <ProfileForm agent={a} canEdit={canEdit} onSaved={onChange} />
          <AgentOnboardingPanel agentId={a.agent_id} canEdit={canEdit} onUpdate={onChange} />
        </>
      )}
    </div>
  );
}

const PROFILE_FIELDS = ['license_number', 'license_state', 'license_expiry', 'ce_hours_completed', 'ce_hours_required', 'ce_cycle_end', 'nrds_id', 'mls_id', 'dba', 'bio'];

function ProfileForm({ agent, canEdit, onSaved }) {
  const init = () => ({
    ...Object.fromEntries(PROFILE_FIELDS.map((k) => [k, agent[k] ?? ''])),
    designations: (agent.designations || []).join(', '),
  });
  const [f, setF] = useState(init);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    if (!f.license_number.trim()) { toast({ title: 'License number is required' }); return; }
    setSaving(true);
    try {
      await api.patch(`/re/agents/${agent.agent_id}`, {
        ...f,
        license_number: f.license_number.trim(),
        ce_hours_completed: Number(f.ce_hours_completed) || 0,
        ce_hours_required: Number(f.ce_hours_required) || 0,
        designations: f.designations.split(',').map((d) => d.trim()).filter(Boolean),
      });
      toast({ title: 'Agent profile saved' });
      onSaved();
    } catch (e) {
      toast({ title: 'Could not save the profile', description: errorMessage(e) });
    } finally { setSaving(false); }
  };
  const id = (k) => `agent-${agent.agent_id}-${k}`;
  return (
    <div className="border-t border-slate-100 p-4" data-testid={`profile-${agent.agent_id}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>Profile & licence</div>
      <fieldset disabled={!canEdit} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div><Label htmlFor={id('lic')} className="text-xs">License number</Label><Input id={id('lic')} value={f.license_number} onChange={set('license_number')} className="mt-1" data-testid="profile-license" /></div>
        <StateSelect label="License state" value={f.license_state} onChange={(v) => setF((x) => ({ ...x, license_state: v }))} testId="profile-state" />
        <div><Label htmlFor={id('exp')} className="text-xs">License expires</Label><Input id={id('exp')} type="date" value={f.license_expiry} onChange={set('license_expiry')} className="mt-1" data-testid="profile-expiry" /></div>
        <div><Label className="text-xs">CE hours completed</Label><NumberInput integer min={0} max={1000} value={f.ce_hours_completed === '' ? null : f.ce_hours_completed} onChange={(n) => setF((x) => ({ ...x, ce_hours_completed: n ?? '' }))} className="mt-1" data-testid="profile-ce-done" /></div>
        <div><Label className="text-xs">CE hours required</Label><NumberInput integer min={0} max={1000} value={f.ce_hours_required === '' ? null : f.ce_hours_required} onChange={(n) => setF((x) => ({ ...x, ce_hours_required: n ?? '' }))} className="mt-1" data-testid="profile-ce-required" /></div>
        <div><Label htmlFor={id('cyc')} className="text-xs">CE cycle ends</Label><Input id={id('cyc')} type="date" value={f.ce_cycle_end} onChange={set('ce_cycle_end')} className="mt-1" data-testid="profile-ce-cycle" /></div>
        <div><Label htmlFor={id('nrds')} className="text-xs">NRDS ID</Label><Input id={id('nrds')} value={f.nrds_id} onChange={set('nrds_id')} className="mt-1" /></div>
        <div><Label htmlFor={id('mls')} className="text-xs">MLS ID</Label><Input id={id('mls')} value={f.mls_id} onChange={set('mls_id')} className="mt-1" /></div>
        <div><Label htmlFor={id('dba')} className="text-xs">DBA</Label><Input id={id('dba')} value={f.dba} onChange={set('dba')} className="mt-1" /></div>
        <div className="sm:col-span-2 lg:col-span-3"><Label htmlFor={id('des')} className="text-xs">Designations (comma separated, e.g. ABR, CRS)</Label><Input id={id('des')} value={f.designations} onChange={set('designations')} className="mt-1" /></div>
        <div className="sm:col-span-2 lg:col-span-3"><Label htmlFor={id('bio')} className="text-xs">Bio</Label><textarea id={id('bio')} value={f.bio} onChange={set('bio')} maxLength={2000} rows={3} className="mt-1 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" /></div>
      </fieldset>
      {canEdit
        ? <button onClick={save} disabled={saving} data-testid="profile-save" className="mt-3 text-xs font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-50" style={{ background: '#6BA82E' }}>{saving ? 'Saving…' : 'Save profile'}</button>
        : <p className="text-[11px] text-slate-400 mt-3">Only this agent or an admin can edit the profile.</p>}
    </div>
  );
}

function AddAgentModal({ existing, onClose, onAdded }) {
  const [staff, setStaff] = useState(null);
  const [f, setF] = useState({ user_id: '', license_number: '', license_state: '', license_expiry: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/crm/staff').then((r) => setStaff(r.data?.items || [])).catch(() => setStaff([]));
  }, []);
  const options = (staff || []).filter((s) => !existing.includes(s.user_id)).map((s) => ({ value: s.user_id, label: `${s.name || s.email}${s.name && s.email ? ` (${s.email})` : ''}` }));
  const submit = async () => {
    if (!f.user_id) { setError('Pick the person to add'); return; }
    if (!f.license_number.trim()) { setError('Enter their license number'); return; }
    setError(''); setSaving(true);
    try {
      await api.post('/re/agents', null, {
        params: {
          user_id: f.user_id,
          license_number: f.license_number.trim(),
          license_state: f.license_state || undefined,
          license_expiry: f.license_expiry || undefined,
        },
      });
      toast({ title: 'Agent added', description: 'Their onboarding checklist is ready.' });
      onAdded();
    } catch (e) {
      setError(errorMessage(e, 'Could not add the agent'));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Add agent">
        <div className="font-serif text-lg font-bold mb-4" style={{ color: '#070B5F' }}>Add agent</div>
        <div className="space-y-3">
          {staff === null && <div className="text-xs text-slate-400">Loading your team…</div>}
          {staff !== null && options.length === 0 && (
            <p className="text-xs text-slate-500" data-testid="add-agent-nobody">Everyone on your team already has an agent profile. Invite more people in Organization settings first.</p>
          )}
          {options.length > 0 && <PickSelect label="Team member" value={f.user_id} onChange={(v) => setF((x) => ({ ...x, user_id: v }))} options={options} placeholder="Pick a person" testId="add-agent-user" />}
          <div><Label className="text-xs">License number</Label><Input value={f.license_number} onChange={(e) => setF((x) => ({ ...x, license_number: e.target.value }))} className="mt-1" data-testid="add-agent-license" /></div>
          <StateSelect label="License state" value={f.license_state} onChange={(v) => setF((x) => ({ ...x, license_state: v }))} testId="add-agent-state" />
          <div><Label className="text-xs">License expires</Label><Input type="date" value={f.license_expiry} onChange={(e) => setF((x) => ({ ...x, license_expiry: e.target.value }))} className="mt-1" data-testid="add-agent-expiry" /></div>
          {error && <div className="text-xs text-red-600" data-testid="add-agent-error">{error}</div>}
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving || options.length === 0} className="text-sm px-4 py-2 rounded-lg text-white disabled:opacity-50" style={{ background: '#6BA82E' }} data-testid="add-agent-submit">{saving ? 'Adding…' : 'Add agent'}</button>
        </div>
      </div>
    </div>
  );
}

function AgentOnboardingPanel({ agentId, canEdit, onUpdate }) {
  const [ob, setOb] = useState(null);
  const [error, setError] = useState('');
  const reload = () => api.get(`/re/agents/${agentId}/onboarding`).then((r) => { setOb(r.data); setError(''); }).catch((e) => setError(errorMessage(e, 'Could not load the checklist')));
  useEffect(() => { reload(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (key, completed) => {
    try {
      await api.patch(`/re/agents/${agentId}/onboarding/${key}`, null, { params: { completed } });
      await reload();
      onUpdate();
    } catch (e) {
      toast({ title: 'Could not update the checklist', description: errorMessage(e) });
    }
  };
  if (error) return <div className="border-t border-slate-100 p-4 text-sm text-red-600">{error}</div>;
  if (!ob) return <div className="border-t border-slate-100 p-4 text-slate-400 text-sm">Loading…</div>;
  const grouped = ob.items.reduce((acc, i) => { (acc[i.category] = acc[i.category] || []).push(i); return acc; }, {});
  return (
    <div className="border-t border-slate-100 p-4 space-y-3">
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: '#6BA82E' }}>{cat}</div>
          <div className="space-y-1">
            {items.map((i) => (
              <label key={i.key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-testid={`onboarding-item-${i.key}`}>
                <input type="checkbox" checked={!!i.completed} disabled={!canEdit} onChange={(e) => toggle(i.key, e.target.checked)} />
                <span className={`text-sm ${i.completed ? 'line-through text-slate-400' : ''}`} style={{ color: i.completed ? undefined : '#070B5F' }}>{i.label}</span>
                {i.required && <span className="text-[9px] uppercase font-bold text-red-500">Req</span>}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
